import { Input, Button, Checkbox, TooltipWithIcon } from 'ui';
import {
  StyledEditCreateUserDrawer,
  StyledFooter,
} from 'ui/Drawer/Drawers/style';
import { useIntl } from 'react-intl';
import useQueryApiClient from 'utils/useQueryApiClient';
import { Form } from 'antd';
import React, { useState } from 'react';

interface CreateFarmUserDrawerProps {
  userEditId: number | undefined;
  setUserEditId: React.Dispatch<React.SetStateAction<number | undefined>>;
  reload: number;
  setReload: React.Dispatch<React.SetStateAction<number>>;
  setShowCreateEditDrawer: React.Dispatch<React.SetStateAction<boolean>>;
  showCreateEditDrawer: boolean;
}

interface PermissionProps {
  id: number;
  name: string;
  display_name: string;
}

const EditCreateUserDrawer = ({
  userEditId,
  setUserEditId,
  reload,
  setReload,
  setShowCreateEditDrawer,
  showCreateEditDrawer,
}: CreateFarmUserDrawerProps) => {
  const [permissions, setPermissions] = useState<PermissionProps[]>([]);

  const intl = useIntl();
  const [form] = Form.useForm();

  useQueryApiClient({
    request: {
      url: `api/v2/farm-users/${userEditId}`,
    },
    enabled: !!userEditId,
    onSuccess: (response) => {
      form.setFieldsValue(response);
    },
  });

  useQueryApiClient({
    request: {
      url: 'api/v2/permissions',
    },
    onSuccess: (response) => {
      setPermissions(response);
    },
  });

  const handleClose = () => {
    setUserEditId(undefined);
    setShowCreateEditDrawer(false);
    setReload(reload + 1);
    form.resetFields();
  };

  const { appendData } = useQueryApiClient({
    request: {
      url: userEditId ? `api/v2/users/${userEditId}` : 'api/v2/users',
      method: userEditId ? 'PATCH' : 'POST',
    },
    onSuccess: () => {
      handleClose();
    },
  });

  return (
    <StyledEditCreateUserDrawer
      open={showCreateEditDrawer}
      onClose={handleClose}
      title={intl.formatMessage({
        id: userEditId ? 'general.edit_user' : 'general.create_user',
      })}
      footer={
        <StyledFooter>
          <Button
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={handleClose}
          />
          <Button
            type="primary"
            label={intl.formatMessage({ id: 'general.save' })}
            onClick={() => form.submit()}
          />
        </StyledFooter>
      }
      width={800}
      destroyOnClose
      zIndex={1100}
    >
      <Form form={form} layout="vertical" onFinish={appendData}>
        <Input
          label={intl.formatMessage({ id: 'general.first_name' })}
          name={'name'}
          validations={'required'}
        />
        <Input
          label={intl.formatMessage({ id: 'general.surname' })}
          name={'surname'}
          validations={'required'}
        />
        <Input
          label={
            <span>
              {intl.formatMessage({ id: 'general.personal_code' })}
              <TooltipWithIcon
                title={intl.formatMessage({
                  id: 'general.personal_code_tooltip',
                })}
              />
            </span>
          }
          name={'personal_code'}
          validations={['required', 'personalCode']}
        />
        <Input
          label={intl.formatMessage({ id: 'general.email' })}
          name={'email'}
          validations={['required', 'email']}
        />
        <Input
          label={intl.formatMessage({ id: 'general.telephone' })}
          name={'phone'}
        />
        <Input
          label={intl.formatMessage({ id: 'general.position' })}
          name={'position'}
          validations={'required'}
        />
        <Checkbox
          label={intl.formatMessage({ id: 'general.status' })}
          name={'is_active'}
          initialValue={true}
        />
        <Checkbox
          name={'permissions'}
          options={permissions.map((permission) => {
            return {
              value: permission.name,
              label: permission.display_name,
            };
          })}
        />
      </Form>
    </StyledEditCreateUserDrawer>
  );
};

export default EditCreateUserDrawer;
